// The REAL wire shapes of the Wave 2 endpoints (crates/api service/defs/dto.rs, routes/account.rs;
// crates/region-api persons.rs, defs/*, sql.rs; packages/contracts/schemas/defs-*.schema.json) and the
// mapping into the console's view types in schemas.ts. Screens never see a wire shape: the HTTP client
// parses with these schemas and hands back console types; the fixture adapter produces console types
// directly. When a contract moves, this file is the one to change.
import { z } from "zod";
import type { QueryIr } from "./ir";
import type {
  CatalogEvent,
  CatalogProperty,
  Cohort,
  CohortCriterion,
  CohortDefinition,
  CohortSize,
  Dashboard,
  DashboardSummary,
  DashboardTile,
  GroupType,
  GroupsPage,
  IdentityGraph,
  LiveEvent,
  Person,
  PersonEvent,
  PersonEventsPage,
  PersonSummary,
  PersonsPage,
  SavedInsight,
  SqlResult,
  UnmergeResult,
  UserRef,
} from "./schemas";

const id = z.string().min(1);
const datetime = z.string().min(1);
const props = z.record(z.string(), z.unknown());

/** A user id only: names are resolved by the screen from the org's members. */
const userRef = (u: string | null | undefined): UserRef => (u ? { id: u, name: "", email: "" } : null);

// ── Insights ─────────────────────────────────────────────────────────────────
export const InsightWire = z.object({
  id,
  project_id: id,
  name: z.string(),
  description: z.string().nullable(),
  query: z.custom<QueryIr>((v) => typeof v === "object" && v !== null && "insight" in v, "Expected a Query IR"),
  viz: z.record(z.string(), z.unknown()),
  tags: z.array(z.string()),
  favorited: z.boolean(),
  created_via: z.enum(["ui", "api", "mcp", "ai"]),
  created_by: z.string().nullable(),
  created_at: datetime,
  updated_at: datetime,
});
export type InsightWire = z.infer<typeof InsightWire>;
export const InsightListWire = z.object({ insights: z.array(InsightWire), next_offset: z.number().nullable() });

export function toSavedInsight(w: InsightWire): SavedInsight {
  const display = w.viz.display === "bar" || w.viz.display === "line" ? w.viz.display : undefined;
  return {
    id: w.id,
    project_id: w.project_id,
    name: w.name,
    description: w.description,
    query_ir: w.query,
    ir_version: w.query.ir_version,
    viz: display ? { display } : {},
    tags: w.tags,
    favorite: w.favorited,
    created_via: w.created_via,
    created_by: userRef(w.created_by),
    created_at: w.created_at,
    updated_at: w.updated_at,
  };
}

// ── Dashboards ───────────────────────────────────────────────────────────────
const LayoutWire = z.object({ x: z.number(), y: z.number(), w: z.number(), h: z.number() });
export const TileWire = z.object({
  id,
  kind: z.enum(["insight", "text"]),
  insight_id: z.string().nullable(),
  insight: InsightWire.nullable(),
  body: z.string().nullable(),
  layout: LayoutWire,
  created_at: datetime,
});
export const DashboardSummaryWire = z.object({
  id,
  name: z.string(),
  description: z.string().nullable(),
  is_home: z.boolean(),
  tags: z.array(z.string()),
  tile_count: z.number(),
  created_at: datetime,
  updated_at: datetime,
});
export const DashboardListWire = z.object({ dashboards: z.array(DashboardSummaryWire) });
const DateRangeWire = z.object({ from: z.string(), to: z.string().optional() }).passthrough();
export const DashboardWire = z.object({
  id,
  project_id: id,
  name: z.string(),
  description: z.string().nullable(),
  is_home: z.boolean(),
  tags: z.array(z.string()),
  date_range: DateRangeWire.nullable(),
  filters: z.unknown().nullable(),
  tiles: z.array(TileWire),
  created_by: z.string().nullable(),
  created_at: datetime,
  updated_at: datetime,
});

/** POST /app/projects/{p}/dashboard-templates/{id}: 201 created, 200 already there. */
export const TemplateApplyWire = z.object({ created: z.boolean(), dashboard: DashboardWire });

/** The console's grid caps tile height at 8 rows; the API allows 24. */
const clampLayout = (l: z.infer<typeof LayoutWire>) => ({ x: l.x, y: l.y, w: l.w, h: Math.min(8, Math.max(1, l.h)) });

export const toTile = (t: z.infer<typeof TileWire>): DashboardTile => ({
  id: t.id,
  kind: t.kind,
  insight_id: t.insight_id,
  insight: t.insight ? toSavedInsight(t.insight) : null,
  body: t.body,
  layout: clampLayout(t.layout),
  created_at: t.created_at,
});

export const toDashboardSummary = (projectId: string, d: z.infer<typeof DashboardSummaryWire>): DashboardSummary => ({
  id: d.id,
  project_id: projectId,
  name: d.name,
  description: d.description,
  pinned: d.is_home,
  tile_count: d.tile_count,
  created_by: null,
  created_at: d.created_at,
  updated_at: d.updated_at,
});

export const toDashboard = (d: z.infer<typeof DashboardWire>): Dashboard => ({
  id: d.id,
  project_id: d.project_id,
  name: d.name,
  description: d.description,
  pinned: d.is_home,
  tile_count: d.tiles.length,
  created_by: userRef(d.created_by),
  created_at: d.created_at,
  updated_at: d.updated_at,
  filters: d.date_range ? { date_range: { from: d.date_range.from, to: d.date_range.to } } : {},
  tiles: d.tiles.map(toTile),
});

// ── Cohorts ──────────────────────────────────────────────────────────────────
const WindowWire = z.object({ value: z.number().int(), unit: z.enum(["day", "week", "month"]) });
const BehavioralWire = z.object({
  type: z.literal("behavioral"),
  event: z.string().nullable(),
  filters: z.unknown().optional(),
  operator: z.enum(["performed", "not_performed"]),
  count_operator: z.enum(["gte", "lte", "eq"]).optional(),
  count: z.number().int().optional(),
  window: WindowWire,
});
const PersonPropertyWire = z.object({
  type: z.literal("person_property"),
  filter: z.object({ type: z.string(), key: z.string(), operator: z.string(), value: z.unknown().optional() }),
});
export const CohortDefinitionWire = z.object({
  op: z.enum(["and", "or"]),
  criteria: z.array(z.discriminatedUnion("type", [BehavioralWire, PersonPropertyWire])),
});
export type CohortDefinitionWire = z.infer<typeof CohortDefinitionWire>;

const WINDOW_DAYS = { day: 1, week: 7, month: 31 } as const;

/** Console editor model → contract (`defs-cohort.schema.json`). "Exactly/at most 0 times" = not performed. */
export function toCohortDefinitionWire(d: CohortDefinition): CohortDefinitionWire {
  return {
    op: d.match === "all" ? "and" : "or",
    criteria: d.criteria.map((c) =>
      c.type === "behavioral"
        ? c.count === 0 && c.operator !== "gte"
          ? { type: "behavioral", event: c.event, operator: "not_performed", window: { value: c.days, unit: "day" } }
          : {
              type: "behavioral",
              event: c.event,
              operator: "performed",
              count_operator: c.operator,
              count: Math.max(1, c.count),
              window: { value: c.days, unit: "day" },
            }
        : {
            type: "person_property",
            filter: {
              type: "person",
              key: c.key,
              operator: c.operator,
              ...(c.operator === "is_set" || c.operator === "is_not_set" ? {} : { value: c.value ?? "" }),
            },
          },
    ),
  };
}

/** Contract → console editor model. Event-property filters inside a behavioural criterion are not editable here. */
export function fromCohortDefinitionWire(w: CohortDefinitionWire): CohortDefinition {
  const criteria: CohortCriterion[] = w.criteria.map((c) => {
    if (c.type === "behavioral") {
      const days = Math.min(365, c.window.value * WINDOW_DAYS[c.window.unit]);
      return c.operator === "not_performed"
        ? { type: "behavioral", event: c.event ?? "", operator: "eq", count: 0, days }
        : { type: "behavioral", event: c.event ?? "", operator: c.count_operator ?? "gte", count: c.count ?? 1, days };
    }
    const v = c.filter.value;
    return {
      type: "person_property",
      key: c.filter.key,
      operator: c.filter.operator as Extract<CohortCriterion, { type: "person_property" }>["operator"],
      ...(typeof v === "string" || typeof v === "number" || typeof v === "boolean" ? { value: v } : {}),
    };
  });
  return { match: w.op === "and" ? "all" : "any", criteria };
}

const CalculationWire = z.object({
  status: z.string(),
  environment: z.string(),
  size: z.number().nullable(),
  cohort_version: z.number().nullable(),
  calculated_at: datetime,
  error: z.string().nullable(),
});
export type CalculationWire = z.infer<typeof CalculationWire>;
export const CohortWire = z.object({
  id,
  project_id: id,
  name: z.string(),
  description: z.string().nullable(),
  kind: z.enum(["dynamic", "static"]),
  definition: CohortDefinitionWire.nullable(),
  version: z.number().int(),
  calculation: CalculationWire.nullable(),
  created_by: z.string().nullable(),
  created_at: datetime,
  updated_at: datetime,
});
export type CohortWire = z.infer<typeof CohortWire>;
export const CohortListWire = z.object({ cohorts: z.array(CohortWire) });

export const toCohort = (c: CohortWire): Cohort => ({
  id: c.id,
  project_id: c.project_id,
  name: c.name,
  description: c.description,
  kind: c.kind,
  definition: c.definition ? fromCohortDefinitionWire(c.definition) : null,
  version: c.version,
  last_calculated_at: c.calculation?.calculated_at ?? null,
  created_by: userRef(c.created_by),
  created_at: c.created_at,
  updated_at: c.updated_at,
});

/** Recalculate sizes older than this (membership moves as people act). */
const SIZE_MAX_AGE_MS = 60 * 60_000;
/** Recording a calculation touches the row too: `updated_at` lands a moment after `calculated_at`. */
const SAME_WRITE_MS = 5_000;

/**
 * A stored calculation is usable for `env` when it succeeded there, after the last definition change,
 * within the last hour. `justCalculated` skips the age checks for the answer of a calculate call.
 */
export function freshSize(c: CohortWire, env: string, justCalculated = false, now = Date.now()): CohortSize | null {
  const calc = c.calculation;
  if (!calc || calc.status !== "ok" || calc.environment !== env || calc.size === null) return null;
  const at = new Date(calc.calculated_at).getTime();
  if (!justCalculated && (at < new Date(c.updated_at).getTime() - SAME_WRITE_MS || now - at > SIZE_MAX_AGE_MS)) return null;
  return { count: calc.size, total: null, calculated_at: calc.calculated_at };
}

export const CohortPreviewWire = z.object({ count: z.number(), total: z.number().nullable(), calculated_at: datetime });
export const StaticUploadWire = z.object({ cohort_id: id, received: z.number(), inserted: z.number(), total: z.number() });

// ── Catalog ──────────────────────────────────────────────────────────────────
const CatalogPropertyWire = z.object({
  id,
  event_id: z.string().nullable(),
  scope: z.enum(["event", "person", "group", "session"]),
  name: z.string(),
  type: z.enum(["string", "number", "bool", "datetime", "money_minor", "currency", "enum", "id"]),
  enum_values: z.array(z.string()).nullable(),
  required: z.boolean(),
  pii: z.enum(["none", "pseudonymous", "personal", "sensitive"]),
  description: z.string().nullable(),
  example: z.string().nullable(),
});
export type CatalogPropertyWire = z.infer<typeof CatalogPropertyWire>;
export const CatalogEventWire = z.object({
  id,
  project_id: id,
  name: z.string(),
  display_name: z.string().nullable(),
  description: z.string().nullable(),
  question: z.string().nullable(),
  kind: z.string(),
  status: z.enum(["planned", "implemented", "verified", "deprecated", "unplanned"]),
  owner_id: z.string().nullable(),
  platforms: z.array(z.string()),
  tags: z.array(z.string()),
  pii_level: z.enum(["none", "pseudonymous", "personal", "sensitive"]),
  first_seen_at: datetime.nullable(),
  last_seen_at: datetime.nullable(),
  volume_30d: z.number().nullable(),
  properties: z.array(CatalogPropertyWire),
  version: z.number(),
  created_at: datetime,
  updated_at: datetime,
});
export type CatalogEventWire = z.infer<typeof CatalogEventWire>;
export const CatalogListWire = z.object({ events: z.array(CatalogEventWire) });
export const CatalogSyncWire = z.object({
  environment: z.string(),
  discovered: z.number(),
  implemented: z.array(z.string()),
  unplanned: z.array(z.string()),
  updated: z.number(),
  synced_at: datetime,
});
export type CatalogSyncResult = z.infer<typeof CatalogSyncWire>;

export const toCatalogEvent = (e: CatalogEventWire): CatalogEvent => ({
  id: e.id,
  project_id: e.project_id,
  name: e.name,
  display_name: e.display_name,
  description: e.description,
  question: e.question,
  status: e.status,
  owner: userRef(e.owner_id),
  platforms: e.platforms,
  tags: e.tags,
  pii_level: e.pii_level,
  first_seen_at: e.first_seen_at,
  last_seen_at: e.last_seen_at,
  updated_at: e.updated_at,
});

export const toCatalogProperties = (e: CatalogEventWire): CatalogProperty[] =>
  e.properties.map((p) => ({
    id: p.id,
    project_id: e.project_id,
    scope: p.scope,
    event_name: p.event_id ? e.name : null,
    name: p.name,
    type: p.type,
    pii: p.pii,
    description: p.description,
    required: p.required,
    updated_at: e.updated_at,
  }));

/** The event PATCH replaces its property list; this is one property of it as input. */
export const propertyInput = (p: CatalogPropertyWire) => ({
  name: p.name,
  type: p.type,
  ...(p.enum_values ? { enum_values: p.enum_values } : {}),
  required: p.required,
  pii: p.pii,
  ...(p.description !== null ? { description: p.description } : {}),
  ...(p.example !== null ? { example: p.example } : {}),
});

// ── Live events ──────────────────────────────────────────────────────────────
export const LiveEventWire = z.object({
  uuid: z.string(),
  event: z.string(),
  timestamp: z.string(),
  distinct_id: z.string(),
  person_id: z.string().nullable(),
  session_id: z.string().nullable(),
  lib: z.string().nullable(),
  properties: props,
});
export const toLiveEvent = (e: z.infer<typeof LiveEventWire>): LiveEvent => {
  const platform = e.properties.$platform ?? e.properties.$os ?? e.lib;
  return {
    uuid: e.uuid,
    event: e.event,
    distinct_id: e.distinct_id,
    timestamp: e.timestamp,
    platform: typeof platform === "string" && platform ? platform : null,
    properties: e.properties,
  };
};

// ── People ───────────────────────────────────────────────────────────────────
/** ClickHouse `YYYY-MM-DD hh:mm:ss.sss` (UTC) → ISO 8601. */
export function chTimeToIso(t: string): string {
  return /^\d{4}-\d\d-\d\d \d\d:\d\d:\d\d/.test(t) ? `${t.replace(" ", "T")}Z` : t;
}

const str = (v: unknown) => (typeof v === "string" && v.trim() ? v.trim() : null);
function nameAndEmail(p: Record<string, unknown>): { name: string | null; email: string | null } {
  return { name: str(p.$name) ?? str(p.name), email: str(p.$email) ?? str(p.email) };
}

export const PersonsWire = z.object({
  persons: z.array(
    z.object({
      id,
      created_at: datetime,
      is_identified: z.boolean(),
      properties: props,
      distinct_ids: z.array(z.string()),
    }),
  ),
  next_cursor: z.string().nullable(),
});
export const toPersonsPage = (w: z.infer<typeof PersonsWire>): PersonsPage => ({
  persons: w.persons.map(
    (p): PersonSummary => ({
      id: p.id,
      ...nameAndEmail(p.properties),
      distinct_ids: p.distinct_ids,
      distinct_id_count: p.distinct_ids.length,
      is_identified: p.is_identified,
      properties: p.properties,
      created_at: p.created_at,
      last_seen_at: null,
    }),
  ),
  next_cursor: w.next_cursor,
});

const EventRowWire = z.object({
  uuid: z.string(),
  event: z.string(),
  timestamp: z.string(),
  distinct_id: z.string(),
  properties: props,
});
const toPersonEvent = (e: z.infer<typeof EventRowWire>): PersonEvent => ({
  uuid: e.uuid,
  event: e.event,
  timestamp: chTimeToIso(e.timestamp),
  distinct_id: e.distinct_id,
  properties: e.properties,
});

export const PersonWire = z.union([
  z.object({
    id,
    created_at: datetime,
    is_identified: z.boolean(),
    properties: props,
    distinct_ids: z.array(z.object({ distinct_id: z.string(), identified: z.boolean() })),
    events: z.array(EventRowWire),
  }),
  z.object({ id, merged_into: id }),
]);
export function toPerson(w: z.infer<typeof PersonWire>): Person {
  if ("merged_into" in w) {
    return {
      id: w.id,
      name: null,
      email: null,
      distinct_ids: [],
      distinct_id_count: 0,
      is_identified: false,
      properties: {},
      created_at: new Date(0).toISOString(),
      last_seen_at: null,
      updated_at: new Date(0).toISOString(),
      merged_into: w.merged_into,
    };
  }
  const last = w.events[0];
  return {
    id: w.id,
    ...nameAndEmail(w.properties),
    distinct_ids: w.distinct_ids.map((d) => d.distinct_id),
    distinct_id_count: w.distinct_ids.length,
    is_identified: w.is_identified,
    properties: w.properties,
    created_at: w.created_at,
    last_seen_at: last ? chTimeToIso(last.timestamp) : null,
    updated_at: w.created_at,
    merged_into: null,
  };
}

export const PersonEventsWire = z.object({ events: z.array(EventRowWire), next_cursor: z.string().nullable() });
export const toPersonEventsPage = (w: z.infer<typeof PersonEventsWire>): PersonEventsPage => ({
  events: w.events.map(toPersonEvent),
  next_cursor: w.next_cursor,
});

export const IdentityWire = z.object({
  person_id: id,
  merged_into: z.string().nullable(),
  nodes: z.array(
    z.object({ distinct_id: z.string(), identified: z.boolean().optional(), member: z.boolean(), blocked: z.boolean() }),
  ),
  edges: z.array(
    z.object({
      id: z.number().int(),
      kind: z.string(),
      from: z.string(),
      to: z.string(),
      outcome: z.string(),
      source_event_uuid: z.string().nullable(),
      created_at: datetime,
      reverted_at: datetime.nullable(),
      reverted_by: z.string().nullable(),
      revert_reason: z.string().nullable(),
      unmergeable: z.boolean(),
    }),
  ),
  audit: z.array(
    z.object({
      id: z.number().int(),
      kind: z.string(),
      actor: z.string(),
      reason: z.string().nullable(),
      edge_id: z.number().int().nullable(),
      before: z.unknown(),
      after: z.unknown(),
      created_at: datetime,
    }),
  ),
});
export const toIdentityGraph = (w: z.infer<typeof IdentityWire>): IdentityGraph => ({
  person_id: w.person_id,
  nodes: w.nodes.map((n) => ({ distinct_id: n.distinct_id, is_identified: n.identified ?? false, member: n.member, blocked: n.blocked })),
  edges: w.edges.map((e) => ({
    id: e.id,
    from_distinct_id: e.from,
    to_distinct_id: e.to,
    kind: e.kind,
    outcome: e.outcome,
    source_event_uuid: e.source_event_uuid,
    created_at: e.created_at,
    reverted_at: e.reverted_at,
    reverted_by: e.reverted_by,
    reason: e.revert_reason,
    unmergeable: e.unmergeable,
  })),
  audit: w.audit.map((a) => ({
    id: a.id,
    kind: a.kind,
    actor: a.actor,
    reason: a.reason,
    edge_id: a.edge_id,
    created_at: a.created_at,
    before_snapshot: a.before,
    after_snapshot: a.after,
  })),
});

export const UnmergeWire = z.object({
  edge_id: z.number().int(),
  persons: z.array(z.object({ id, distinct_ids: z.array(z.string()) })),
});
export const toUnmergeResult = (personId: string, w: z.infer<typeof UnmergeWire>): UnmergeResult => ({
  edge_id: w.edge_id,
  split_person_ids: w.persons.map((p) => p.id).filter((p) => p !== personId),
});

const GroupTypeWire = z.object({
  group_type_index: z.number().int(),
  group_type: z.string(),
  name_singular: z.string().nullable(),
  name_plural: z.string().nullable(),
});
export const GroupTypesWire = z.object({ group_types: z.array(GroupTypeWire) });
export const toGroupType = (t: z.infer<typeof GroupTypeWire>): GroupType => ({
  index: t.group_type_index,
  group_type: t.group_type,
  name_singular: t.name_singular,
  name_plural: t.name_plural,
});
export const GroupsWire = z.object({
  groups: z.array(
    z.object({
      group_type_index: z.number().int(),
      group_key: z.string(),
      properties: props,
      created_at: datetime,
      updated_at: datetime,
    }),
  ),
});
export const toGroupsPage = (w: z.infer<typeof GroupsWire>): GroupsPage => ({ groups: w.groups, next_cursor: null });

// ── SQL ──────────────────────────────────────────────────────────────────────
export const SqlWire = z.object({
  columns: z.array(z.object({ name: z.string(), type: z.string() })),
  rows: z.array(z.array(z.unknown())),
  stats: z.object({
    row_count: z.number(),
    limit: z.number(),
    truncated: z.boolean(),
    rows_read: z.number(),
    bytes_read: z.number(),
    elapsed_ms: z.number(),
    cached: z.boolean(),
  }),
});
export const toSqlResult = (w: z.infer<typeof SqlWire>): SqlResult => ({
  columns: w.columns,
  rows: w.rows,
  truncated: w.stats.truncated,
  meta: { rows_read: w.stats.rows_read, bytes_read: w.stats.bytes_read, elapsed_ms: w.stats.elapsed_ms },
});

// ── Billing (crates/billing engine Overview, served by GET /app/orgs/{org}/billing) ────────────────
const money = z.number().int();
const InvoicePreviewWire = z.object({
  plan: z.string(),
  currency: z.string(),
  base_minor: money,
  events_total: z.number(),
  events_billable: z.number(),
  events_included: z.number(),
  overage_events: z.number(),
  overage_minor: money,
  uncapped_total_minor: money,
  spend_cap_minor: money,
  total_minor: money,
});
export const BillingOverview = z.object({
  org_id: id,
  plan: z.object({
    code: z.string(),
    name: z.string(),
    price_minor: money,
    currency: z.string(),
    events_included: z.number(),
    replays_included: z.number(),
    ai_questions: z.number(),
    retention_days: z.number(),
    custom: z.boolean(),
  }),
  status: z.string(),
  period_start: datetime,
  period_end: datetime,
  spend_cap_minor: money,
  min_spend_cap_minor: money,
  currency: z.string(),
  stage: z.string(),
  usage: z.object({ events_stored: z.number(), events_weighted: z.number(), replays: z.number() }),
  invoice: InvoicePreviewWire,
  projected: InvoicePreviewWire,
  events_at_cap: z.number().nullable(),
  grace_limit: z.number().nullable(),
  event_sample_rate: z.number(),
  replay_sample_rate: z.number(),
  stripe_configured: z.boolean(),
  has_billing_account: z.boolean(),
  invoices: z.array(
    z.object({
      id,
      period_start: datetime,
      period_end: datetime,
      plan_code: z.string(),
      currency: z.string(),
      total_minor: money,
      events_billable: z.number(),
      status: z.string(),
    }),
  ),
});
export type BillingOverview = z.infer<typeof BillingOverview>;
export const CheckoutOutcome = z.union([
  z.object({ kind: z.literal("redirect"), url: z.url(), session_id: z.string() }),
  z.object({ kind: z.literal("plan_changed"), plan: z.string(), proration: z.unknown() }),
]);
export type CheckoutOutcome = z.infer<typeof CheckoutOutcome>;
